import { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import { CreateMemberDialog, MemberActions } from "./TeamDialogs";

const TH = "text-amber-400 font-display uppercase tracking-widest";
export const roleLabel = (r) => ({ admin: "Admin", master: "Master", agent: "Agent", client: "Client" }[r] || r);

export default function TeamPanel({ onChanged }) {
  const { user, refreshUser } = useAuth();
  const [parentId, setParentId] = useState(null);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (parentId) params.set("parent_id", parentId);
    if (q) params.set("q", q);
    try {
      const r = await api.get(`/team/members?${params}`);
      setData(r.data);
    } catch (e) { toast.error(formatError(e)); }
  }, [parentId, q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const changed = () => { load(); refreshUser(); onChanged?.(); };
  const childRole = data?.child_role;

  return (
    <div className="space-y-4" data-testid="team-panel">
      <div className="flex items-center gap-1 text-xs font-display uppercase tracking-widest flex-wrap" data-testid="team-breadcrumb">
        <button onClick={() => setParentId(null)} className={`px-2 py-1 rounded ${!parentId ? "text-amber-300" : "text-zinc-400 hover:text-amber-300"}`} data-testid="crumb-root">
          My {roleLabel(user?.role === "admin" ? "master" : childRole)}s
        </button>
        {data?.trail.map((t) => (
          <span key={t.id} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-zinc-600" />
            <button onClick={() => setParentId(t.id)} className="px-2 py-1 rounded text-zinc-300 hover:text-amber-300">
              {t.display_name} <span className="text-zinc-500">({roleLabel(t.role)})</span>
            </button>
          </span>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <Input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-zinc-950 border-amber-500/20" data-testid="team-search" />
        {data?.can_create && childRole && <CreateMemberDialog role={childRole} onCreated={changed} />}
      </div>

      <Card className="bg-zinc-950 border-amber-500/20">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-amber-500/15">
                <TableHead className={TH}>Name</TableHead>
                <TableHead className={TH}>Email</TableHead>
                <TableHead className={TH}>Role</TableHead>
                <TableHead className={TH}>Balance</TableHead>
                <TableHead className={TH}>Downline</TableHead>
                <TableHead className={TH}>Status</TableHead>
                <TableHead className={TH}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-8" data-testid="team-empty">No {roleLabel(childRole)}s yet.</TableCell></TableRow>
              )}
              {data?.items.map((m) => (
                <TableRow key={m.id} className="border-amber-500/10" data-testid={`member-row-${m.id}`}>
                  <TableCell className="font-semibold">{m.display_name}</TableCell>
                  <TableCell className="font-mono-stat text-xs">{m.email}</TableCell>
                  <TableCell><Badge className="bg-zinc-800 text-amber-300">{roleLabel(m.role)}</Badge></TableCell>
                  <TableCell className="font-mono-stat text-amber-300" data-testid={`member-balance-${m.id}`}>{(m.credits || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {m.role !== "client" ? (
                      <button onClick={() => setParentId(m.id)} className="text-xs text-amber-300 hover:underline" data-testid={`view-downline-${m.id}`}>
                        {m.children_count} · View
                      </button>
                    ) : <span className="text-zinc-600 text-xs">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={m.disabled ? "bg-red-500/15 text-red-300 border border-red-500/40" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"}>
                      {m.disabled ? "Disabled" : "Active"}
                    </Badge>
                    {m.must_change_password && <div className="text-[10px] text-amber-400 mt-1">Pw change pending</div>}
                  </TableCell>
                  <TableCell>
                    <MemberActions m={m} canLoad={user?.role === "admin" || m.parent_id === user?.id} onDone={changed} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
